<template>
  <div class="">
    <!-- Hero Section -->
    <div class="relative z-0 overflow-hidden bg-main_blue">
      <!-- Large Circular Background Elements -->
      <div class="absolute bottom-0 left-0 w-full h-full">
        <div
          class="absolute -bottom-1/2 -left-1/4 w-[120%] h-[120%] bg-gradient-to-t from-white/20 via-white/10 to-transparent rounded-full"
        ></div>
        <div
          class="absolute -bottom-2/3 -left-1/3 w-[140%] h-[140%] bg-gradient-to-t from-blue-400/30 via-blue-300/15 to-transparent rounded-full"
        ></div>
      </div>

      <!-- Small Decorative Circles -->
      <div
        class="absolute top-16 right-16 w-12 h-12 bg-blue-300/40 rounded-full opacity-90 hidden md:block"
      ></div>
      <div
        class="absolute bottom-32 left-16 w-16 h-16 bg-blue-300/40 rounded-full hidden md:block"
      ></div>

      <section
        class="min-h-screen bg-blue flex items-center justify-center px-4 py-16"
      >
        <div
          class="flex flex-col items-center justify-center max-w-4xl w-full text-center"
        >
          <!-- Top Content Block -->
          <div class="w-full max-w-3xl text-center mb-6 px-4 mt-14">
            <div
              class="inline-block bg-yellow/10 text-yellow px-4 py-1 rounded-full text-base md:text-xl font-semibold tracking-wide uppercase mb-5"
            >
              Welcome to Mshel Prints
            </div>
            <h1
              class="text-3xl md:text-4xl lg:text-4xl font-extrabold text-white leading-snug mb-4"
            >
              Lasting Impressions...!
            </h1>
            <p class="text-base md:text-lg text-white/90 leading-relaxed mb-8">
              We believe high quality printed materials can significantly enhance brand
              visibility and credibility for businesses, especially those in
              diverse sectors.
            </p>
          </div>

          <!-- Bottom Carousel Block -->
          <div class="relative w-full max-w-3xl">
            <!-- Main Carousel Card -->
            <div
              class="relative w-full h-64 md:h-80 lg:h-[26rem] bg-white rounded-2xl shadow-2xl overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500"
            >
              <div
                class="carousel-slide absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out"
                v-for="(slide, index) in slides"
                :key="index"
                :class="{ 'opacity-100': currentSlide === index }"
              >
                <img
                  :src="slide.image"
                  :alt="slide.alt"
                  class="w-full h-full object-cover"
                />
              </div>

              <!-- MSh logo overlay -->
              <div
                class="absolute top-3 right-3 bg-white/95 rounded-lg px-2.5 py-1.5 shadow-sm"
              >
                <span class="text-blue-900 font-bold text-sm">MS</span>
                <span class="text-yellow font-bold text-sm">h</span>
                <span class="text-gray-500 text-xs">el</span>
                <span class="text-main_blue text-xs">Prints</span>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <button
              @click="prevSlide"
              class="absolute top-1/2 -left-6 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-600 text-lg shadow-lg hover:shadow-xl transition-all duration-300 z-20 hover:scale-110"
            >
              ‹
            </button>
            <button
              @click="nextSlide"
              class="absolute top-1/2 -right-6 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-600 text-lg shadow-lg hover:shadow-xl transition-all duration-300 z-20 hover:scale-110"
            >
              ›
            </button>

            <!-- Indicators -->
            <div
              class="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10"
            >
              <button
                v-for="(slide, index) in slides"
                :key="index"
                @click="goToSlide(index)"
                class="w-2 h-2 rounded-full transition-all duration-300 hover:scale-125"
                :class="currentSlide === index ? 'bg-white' : 'bg-white/50'"
              ></button>
            </div>
          </div>
        </div>
      </section>
    </div>


    <!-- Call to Action Section -->
    <div
      class="text-center opacity-0 animate-fade-in-up -mt-6"
      style="animation-delay: 0.6s; animation-fill-mode: forwards"
    >
      <a
        href="https://mshelprints.com/"
        target="_blank"
        class="inline-flex items-center space-x-4 bg-yellow rounded-full px-6 py-4 shadow-lg border border-none hover:shadow-xl transition-all duration-300 cursor-pointer group"
      >
        <span class="text-white font-semibold text-base">Visit Mshel Prints</span>
        <svg
          class="w-6 h-6 text-white group-hover:translate-x-2 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          ></path>
        </svg>
      </a>
    </div>

    <!-- Other sections -->
    <aboutPrints />
    <printsGallery />
  </div>
</template>

<script>
import aboutPrints from "../components/aboutPrints.vue";
import printsGallery from "../components/printsGallery.vue";

export default {
  name: "MshelHomes",
  components: {
    aboutPrints,
    printsGallery,
  },
  data() {
    return {
      currentSlide: 0,
      slides: [
        {
          image: require('../assets/images/Prints_34.jpg'),
          alt: "Modern house exterior",
        },
        {
          image: require('../assets/images/Prints_29.jpg'),
          alt: "Contemporary house",
        },
        {
          image: require('../assets/images/mshel-print.jpg'),
          alt: "Luxury home",
        },
      ],
      autoSlideInterval: null,
    };
  },
  mounted() {
    this.startAutoSlide();
  },
  beforeUnmount() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  },
  methods: {
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    prevSlide() {
      this.currentSlide =
        this.currentSlide === 0
          ? this.slides.length - 1
          : this.currentSlide - 1;
    },
    goToSlide(index) {
      this.currentSlide = index;
    },
    startAutoSlide() {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, 5000);
    },
    getAvatarColor(index) {
      const colors = [
        "bg-orange-400",
        "bg-red-500",
        "bg-purple-500",
        "bg-blue-500",
        "bg-green-500",
      ];
      return colors[index - 1];
    },
  },
};
</script>

<style scoped>
.carousel-slide {
  transition: opacity 0.5s ease-in-out;
}

/* Custom responsive utilities */
@media (max-width: 768px) {
  .min-h-screen {
    min-height: 100vh;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out;
}
</style>
